import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/ProcurementRecordPaymentForm.jsx.
// Posts to `/payment-vouchers/{voucherId}/payment`
// (ProcurementFulfillmentController::storePayment()), then uploads any
// attached proof-of-payment files as Documents (category: 'payment_proof')
// against the resulting ProcurementPayment via
// `/procurement-payments/{id}/documents`.
interface PaymentVoucher {
  id: number;
  pv_number: string;
  payment_amount: number;
  payment_method: string;
}

interface VendorInvoiceOption {
  id: number;
  invoice_number: string;
  date_paid: string | null;
  payment_reference: string | null;
  payment_voucher: PaymentVoucher | null;
}

type FormErrors = Record<string, string[]>;

export function ProcurementRecordPaymentForm() {
  const { voucherId } = useParams<{ voucherId: string }>();
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<VendorInvoiceOption | null | false>(null);
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    bank_reference: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [proofFiles, setProofFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ data: VendorInvoiceOption[] }>('/vendor-invoices', { params: { per_page: 100 } }).then((res) => {
      const inv = res.data.data.find((i) => String(i.payment_voucher?.id) === voucherId);
      setInvoice(inv ?? false);
      if (inv) {
        setForm((f) => ({
          ...f,
          payment_date: inv.date_paid || f.payment_date,
          bank_reference: inv.payment_reference || f.bank_reference,
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  const set =
    (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });

  const pickProofFile = () => fileInputRef.current?.click();

  const onProofFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProofFiles((files) => [...files, file]);
  };

  const removeProofFile = (index: number) => setProofFiles((files) => files.filter((_, i) => i !== index));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.post<{ id: number }>(`/payment-vouchers/${voucherId}/payment`, form);
      for (const file of proofFiles) {
        const data = new FormData();
        data.append('file', file);
        await api.post(`/procurement-payments/${res.data.id}/documents`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      // A cheque payment method raises a Cheque leg (pending_signature) as
      // a side effect of storePayment() — send the officer straight to the
      // cheque register to start the sign-off, not back to the AP list.
      navigate(invoice && invoice.payment_voucher?.payment_method === 'cheque' ? '/memberships/benefits/cheques' : '/procurement/payments');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Record payment</h1>
      </header>

      <div className="card">
        {invoice === null && <p className="muted">Loading…</p>}
        {invoice === false && <p className="alert alert-error">That payment voucher could not be found.</p>}
        {invoice && (
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <p className="span-2 muted">
              Voucher {invoice.payment_voucher?.pv_number} — {invoice.invoice_number} (
              {fmtMoney(invoice.payment_voucher?.payment_amount ?? 0)})
            </p>
            <label>
              Payment date *
              <input type="date" value={form.payment_date} onChange={set('payment_date')} required />
              {errors.payment_date && <small className="error">{errors.payment_date[0]}</small>}
            </label>
            <label>
              Payment reference
              <input value={form.bank_reference} onChange={set('bank_reference')} />
            </label>
            <div className="span-2">
              <span className="detail-item-label">Proof of payment</span>
              <div className="table-scroll">
                <table>
                  <tbody>
                    {proofFiles.map((file, index) => (
                      <tr key={`${file.name}-${index}`}>
                        <td>{file.name}</td>
                        <td className="right">
                          <button
                            type="button"
                            className="btn btn-danger-ghost"
                            onClick={() => removeProofFile(index)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    {proofFiles.length === 0 && (
                      <tr>
                        <td colSpan={2} className="muted center">
                          No files attached yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <button type="button" className="btn btn-ghost" onClick={pickProofFile} style={{ marginTop: 10 }}>
                + Attach proof of payment
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={onProofFileSelected}
                hidden
              />
            </div>
            <div className="form-actions span-2">
              <Link className="btn btn-ghost" to="/procurement/payments">
                Cancel
              </Link>
              <button className="btn btn-primary" disabled={saving}>
                {saving ? 'Recording…' : 'Record payment'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
